import { GetTripOverviewResponse } from '@orbit/api-types'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { useQuery } from '@tanstack/react-query'

/**
 * Query options for the aggregated member trip-overview endpoint, exported
 * separately so tests can pin the key/URL/freshness policy without mounting the
 * hook.
 *
 * The overview is treated as **never fresh**: its key is deliberately outside
 * every per-entity mutation hook and outside the realtime invalidation map (the
 * trip home never joins the trip's socket room), so an edit made on the
 * Balances or Ideas page would otherwise leave the tiles stale forever.
 * Revalidating on every mount shows the cached bento immediately and replaces
 * it with one cheap request — still far less traffic than the eleven full-list
 * queries the page used to issue.
 */
export const tripOverviewQueryOptions = (tripId: string | undefined) => ({
	queryKey: queryKeys.tripOverview(tripId),
	queryFn: async (): Promise<GetTripOverviewResponse> => {
		if (!tripId) throw new Error('Trip ID is required')
		const response = await api.get<GetTripOverviewResponse>(`/trips/${tripId}/overview`)
		return response.data
	},
	enabled: !!tripId,
	staleTime: 0,
	refetchOnMount: 'always' as const,
})

export const useTripOverview = (tripId: string | undefined) =>
	useQuery(tripOverviewQueryOptions(tripId))
